import { Fragment, useState, useEffect, useRef } from "react";
import { Tab, Nav } from "react-bootstrap";

import { users } from "../../../../services/api/models.ts";
// import styles
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lg-thumbnail.css";
import { ToastContainer, toast } from "react-toastify";

import profile from "../../../../images/profile/profile.png";
import PageTitle from "../../../layouts/PageTitle";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import {
  handleErrorMessage,
  roleData,
  getValidationSchema,
} from "../../../../validation/profileData.validation.js";
import { getApiErrorMessage } from "../../../../utils/getApiErrorMessage";
import { useForm } from "react-hook-form"; // Assuming you're using react-hook-form
import { yupResolver } from "@hookform/resolvers/yup";
import useAccountProgressAPI from "../../../../hooks/useGetAccountProgressAPI.js";
import {
  FIELDS_INTEREST,
  JOB_STATUS,
  POSITIONS,
  STUDY_YEARS,
  EXPERIENCE_LEVELS,
} from "../../../../utils/constants.js";

// Centralized tab items with icons as a const array
const TAB_ITEMS = [
  { key: "Personal", icon: "bi-person", label: "Personal Details" },
  { key: "Contact", icon: "bi-telephone", label: "Contact Info" },
  { key: "Professional", icon: "bi-suitcase-lg", label: "Professional" },
  { key: "Portfolio", icon: "bi-link-45deg", label: "Portfolio & Links" },
  { key: "Location", icon: "bi-geo-alt", label: "Location" },
];

const AppProfile = () => {
  const { loginWithRedirect, user } = useAuth0();
  const divRef = useRef(null);
  const jobStatusDropdownRef = useRef(null);
  const studyYearDropdownRef = useRef(null);
  const positionDropdownRef = useRef(null);
  const experienceYearsDropdownRef = useRef(null);
  const { auth } = useSelector((state) => state.auth);

  const [avatar, setAvatar] = useState("");
  const [clickLoading, setClickLoading] = useState(false);
  const [whereYouKnowUs, setWhereYouKnowUs] = useState([]);
  // NEW: inline edit mode + active tab tracking
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("Personal");
  const [openInterestFields, setOpenInterestFields] = useState(false);
  // Job Status dropdown state
  const [jobStatusDropdownOpen, setJobStatusDropdownOpen] = useState(false);
  const [studyYearDropdownOpen, setStudyYearDropdownOpen] = useState(false);
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);
  const [experienceYearsDropdownOpen, setExperienceYearsDropdownOpen] =
    useState(false);
  const { progress } = useAccountProgressAPI();
  const validationSchema = getValidationSchema(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema), // Integrate Yup validation
    defaultValues: {
      ...auth.user,
      jobStatus: auth.user.jobState, // Map database jobState to form jobStatus
      interestFields: auth.user.interestFields
        ? auth.user.interestFields.split(", ").map((field) => field.trim())
        : [], // Ensure it's an array
      where_you_know_us: auth.user.where_you_know_us
        ? auth.user.where_you_know_us.split(", ").map((field) => field.trim())
        : [],
    },
    context: {
      whereYouKnowUsHasValues: whereYouKnowUs.length > 0,
    },
  });

  const interestFields = watch("interestFields");
  const whereYouKnowUsValues = watch("where_you_know_us");
  // NEW: track current job status from form to drive professional section
  const currentJobStatus = watch("jobStatus");

  // Derived display fields
  const displayName =
    user.username || user.nickname || auth.user.given_name || "";
  const roleLabel = auth.user.role || "";
  const emailValue = auth.user.email || user.email || "";

  // Checklist items for completing profile
  const hasJobStatus = Boolean(
    currentJobStatus && currentJobStatus.trim() !== ""
  );
  const effectiveJobStatus = currentJobStatus || auth.user.jobState;
  const isEffectivelyStudent = effectiveJobStatus === "Student";

  // Use form field (currentJobStatus) for dynamic checklist, fallback to database value
  const completionChecks = [
    // 1. Portfolio Links
    {
      label: "Complete portfolio links",
      done: Boolean(
        auth.user.githubHandle &&
          (auth.user.behanceProfileLink || auth.user.linkedinProfileLink)
      ),
    },
    // 2. Education (only for contestants/students)
    ...(auth.user.role === "contestant"
      ? [
          {
            label: "Complete education information",
            done: Boolean(
              auth.user.university &&
                auth.user.faculty &&
                (isEffectivelyStudent ? auth.user.studyYear : true)
            ),
          },
        ]
      : []),
    // 3. Job/Professional (only show if user has selected a job status AND it's not Student)
    // Fixed professional completion check that considers form state during editing
    ...[
      {
        label: "Complete professional information",
        done: (() => {
          // Use form values when editing, fallback to database values
          const currentJobState = auth.user.jobState;
          const currentInterestFields = auth.user.interestFields
            ? auth.user.interestFields.split(", ")
            : [];
          const currentExperienceYears = auth.user.experienceYears;
          const currentCompanyName = auth.user.companyName;

          // Base requirements for all job statuses
          const hasJobStatus = Boolean(currentJobState);
          const hasInterestFields = Boolean(currentInterestFields?.length > 0);

          // Base completion check (always required)
          if (!hasJobStatus || !hasInterestFields) {
            return false;
          }

          const jobStatus = currentJobState?.toLowerCase() || "";

          // Job-specific requirements
          if (jobStatus.includes("employ")) {
            // Employed: need company name + experience
            return Boolean(currentCompanyName && currentExperienceYears);
          } else if (
            jobStatus === "freelancer" ||
            jobStatus === "currently not working"
          ) {
            // Freelancer/Not Working: need experience only
            return Boolean(currentExperienceYears);
          } else if (jobStatus === "student") {
            // Students: only base requirements (no company or experience needed)
            return true;
          } else {
            // Other job statuses: base requirements are enough
            return false;
          }
        })(),
      },
    ],
    // 4. Location/Address
    {
      label: "Complete location information",
      done: Boolean(auth.user.city && auth.user.country),
    },
  ];

  const completedCount = completionChecks.filter((c) => c.done).length;
  const totalChecks = completionChecks.length;
  const isProfileComplete =
    (progress ?? 0) >= 100 && completedCount >= totalChecks;

  const onSubmit = async (formData) => {
    let finalWhereYouKnowUs = [
      ...formData.where_you_know_us,
      ...(formData.otherAwarenessChannel
        ? [formData.otherAwarenessChannel]
        : []),
    ];
    finalWhereYouKnowUs = finalWhereYouKnowUs.filter(
      (value) => value !== "Other"
    );

    // Auto-set study year to "Graduate" for non-student job statuses
    const processedFormData = {
      ...formData,
      where_you_know_us: finalWhereYouKnowUs,
      studyYear:
        formData.jobStatus && formData.jobStatus.toLowerCase() !== "student"
          ? "Graduate"
          : formData.studyYear,
      // Map jobStatus form field to jobState database field
      jobState: formData.jobStatus,
    };

    // Remove jobStatus since database expects jobState
    delete processedFormData.jobStatus;

    const newData = roleData(processedFormData);
    setClickLoading(true);

    try {
      const requestBody = { ...newData, avatar: avatar || auth.user.picture };
      const headers = { Authorization: "Bearer " + auth.accessToken };
      let Options = { headers: headers };
      const userFilter = { auth0Id: user.sub };

      const response = await users.usersPatch(requestBody, userFilter, Options);

      if (response.status === 200) {
        toast.success("Success update profile");
        setTimeout(() => {
          loginWithRedirect({
            appState: { returnTo: "/app-profile" },
          });
        }, 1000);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      console.log("Error in updating user data", err);
    }
    setClickLoading(false);
  };

  useEffect(() => {
    const currentJobStatus = watch("jobStatus");

    // Only auto-set if user is in editing mode and has selected a job status
    if (isEditing && currentJobStatus) {
      if (currentJobStatus.toLowerCase() !== "student") {
        // Auto-set to Graduate for non-student job statuses
        const currentStudyYear = watch("studyYear");
        if (!currentStudyYear || currentStudyYear !== "Graduate") {
          setValue("studyYear", "Graduate");
        }
      }
    }
  }, [watch("jobStatus"), isEditing, setValue, watch]);

  // Use effect to set the form values if they are fetched or updated asynchronously
  useEffect(() => {
    if (auth.user) {
      for (const key in auth.user) {
        if (key === "interestFields" && typeof auth.user[key] === "string") {
          let interestedArray = [];

          // Check if interestFields is not an empty string
          if (auth.user.interestFields.trim() !== "") {
            // Split the string into an array and trim each field
            interestedArray = auth.user.interestFields
              .split(", ")
              .map((field) => field.trim());
          }
          setValue(key, interestedArray);
        } else if (key === "jobState") {
          // Map database jobState field to form jobStatus field
          setValue("jobStatus", auth.user.jobState);
        } else if (
          key === "where_you_know_us" &&
          typeof auth.user[key] === "string"
        ) {
          let whereYouKnowUsArray = [];

          // Check if where_you_know_us is not an empty string
          if (auth.user.where_you_know_us.trim() !== "") {
            // Split the string into an array and trim each field
            whereYouKnowUsArray = auth.user.where_you_know_us
              .split(", ")
              .map((field) => field.trim());
          }
          setWhereYouKnowUs(whereYouKnowUsArray);
          setValue(key, whereYouKnowUsArray);
        } else {
          setValue(key, auth.user[key]);
        }
      }
      setValue(
        "position",
        auth.user.githubHandle ? POSITIONS[1] : POSITIONS[0]
      );
      setAvatar(auth.user.avatar || auth.user.picture || user.picture || "");
    }
  }, [auth.user, setValue]);

  // Click outside handler for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Interest Fields dropdown
      if (divRef.current && !divRef.current.contains(event.target)) {
        setOpenInterestFields(false);
      }

      // Job Status dropdown
      if (
        jobStatusDropdownRef.current &&
        !jobStatusDropdownRef.current.contains(event.target)
      ) {
        setJobStatusDropdownOpen(false);
      }

      // Study Year dropdown
      if (
        studyYearDropdownRef.current &&
        !studyYearDropdownRef.current.contains(event.target)
      ) {
        setStudyYearDropdownOpen(false);
      }

      // Position dropdown
      if (
        positionDropdownRef.current &&
        !positionDropdownRef.current.contains(event.target)
      ) {
        setPositionDropdownOpen(false);
      }

      // Experience Years dropdown
      if (
        experienceYearsDropdownRef.current &&
        !experienceYearsDropdownRef.current.contains(event.target)
      ) {
        setExperienceYearsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Fragment>
      <PageTitle activeMenu="Profile" motherMenu="App" />
      {/* Local styles for header/progress UI */}
      <style></style>
      <div className="row">
        <div className="col-lg-12">
          <div className="profile card card-body px-3 pt-3 pb-0">
            <div className="profile-head">
              <div className="profile-info profile-banner mb-2 d-flex align-items-center justify-content-between flex-wrap">
                <div className="profile-identity d-flex align-items-center">
                  <div
                    className="profile-photo me-3"
                    style={{ position: "relative" }}
                  >
                    <img
                      src={auth.user.avatar || user.picture || profile}
                      className="rounded-circle avatar"
                      alt="profile"
                    />
                    <i
                      className="bi bi-instagram"
                      style={{
                        position: "absolute",
                        bottom: "-5%",
                        right: "-3%",
                        backgroundColor: "#F93A0B",
                        color: "white",
                        border: "2px solid #F93A0B",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                    ></i>
                  </div>
                  <div className="profile-details d-flex flex-column gap-1">
                    <h4 className="mb-1">{displayName}</h4>
                    <div
                      className="text-orange text-muted mb-1 meta"
                      style={{ fontWeight: 500 }}
                    >
                      {roleLabel}
                    </div>
                    <div className="text-black text-muted email text-break">
                      {emailValue}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress + guidance: two columns when incomplete, full width when complete */}
            <div className="px-1 py-4">
              {isProfileComplete ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-black fw-semibold text-size-m">
                      Profile Completion
                    </span>
                    <span className="profile-completion-percent" style={{ color: "#68e365" }}>
                      {progress}%
                    </span>
                  </div>
                  <div
                    className="progress w-100 progress-bg-gray"
                    style={{ height: "10px" }}
                  >
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${progress}%`, backgroundColor: "#68e365" }}
                    ></div>
                  </div>
                </>
              ) : (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-black text-size-m">
                        Profile Completion
                      </span>
                      <span className="text-size-m text-orange profile-completion-percent">
                        {progress}%
                      </span>
                    </div>
                    <div
                      className="progress w-100 progress-bg-gray"
                      style={{ height: "10px" }}
                    >
                      <div
                        className="progress-bar progress-bar-orange"
                        role="progressbar"
                        style={{ width: `${progress}%` }}
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <div
                      className="p-3 rounded-3 mt-4 mb-2"
                      style={{
                        background: "#FFEDD5",
                        border: "1px solid #F93A0B",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i
                          className="bi bi-exclamation-circle-fill text-orange"
                          style={{ fontSize: "1rem" }}
                        ></i>
                        <span className="text-black">
                          Complete your profile to unlock quest submissions!
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="user-profile-complete">
                      <div className="text-black text-size-m mb-2 complete-profile-label">
                        Complete Your Profile{" "}
                        <span className="profile-completion-percent">
                          ({completedCount}/{totalChecks})
                        </span>
                      </div>
                      <ul className="fw-sm text-black list-unstyled m-0 p-0 d-grid gap-2">
                        {completionChecks.map((c, i) => (
                          <li
                            key={i}
                            className={`d-flex align-items-center gap-2 checklist-item ${
                              c.done ? "done" : ""
                            }`}
                          >
                            <i
                              className={`text-size-m bi ${
                                c.done
                                  ? "bi-check2-circle text-success"
                                  : "bi-circle text-orange"
                              }`}
                            ></i>
                            <span
                              className={
                                c.done
                                  ? "text-success text-decoration-line-through"
                                  : "profile-checklist-label"
                              }
                            >
                              {c.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* NEW: Edit/Save button placed between header and info component, right-aligned */}
      <div className="row">
        <div className="col-12">
          <div
            className="d-flex justify-content-between align-items-center py-3 "
            style={{ flexWrap: "wrap", gap: "1rem" }}
          >
            <h6 className="mb-0 fs-22" style={{ fontWeight: "600" }}>
              Portfolio Information
            </h6>
            <div
              className="d-flex align-items-center"
              style={{ gap: "0.5rem" }}
            >
              {isEditing && (
                <button
                  type="button"
                  className="btn"
                  style={{
                    background: "#6B7280",
                    padding: ".45rem 2rem",
                    fontSize: ".9rem",
                    fontWeight: "600",
                    borderRadius: "25px",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    margin: "0 auto",
                  }}
                  onClick={() => setIsEditing(false)}
                >
                  <i className="bi bi-x-circle" style={{ lineHeight: "0" }}></i>
                  <span>Cancel</span>
                </button>
              )}
              <button
                type="button"
                className={`btn ${isEditing ? "" : "btn-primary"}`}
                style={
                  isEditing
                    ? {
                        padding: ".45rem 1rem",
                        fontSize: ".9rem",
                        fontWeight: "600",
                        borderRadius: "25px",
                        background: "#FFEDD5",
                        color: "#F93A0B",
                        border: "1px solid #F93A0B",
                      }
                    : {
                        padding: ".45rem 1rem",
                        fontSize: ".9rem",
                        fontWeight: "600",
                        borderRadius: "25px",
                      }
                }
                onClick={() => {
                  if (isEditing) {
                    // Trigger save
                    handleSubmit(onSubmit)();
                  } else {
                    setIsEditing(true);
                  }
                }}
              >
                {isEditing ? "Save Changes" : "Edit Account"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Info component card follows */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="profile-tab">
              <div className="custom-tab-1">
                <Tab.Container
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || "Personal")}
                >
                  <Nav as="ul" className="nav  nav-tabs mb-0">
                    {TAB_ITEMS.map((tab) => (
                      <Nav.Item
                        as="li"
                        className="nav-item fw-bold"
                        key={tab.key}
                      >
                        <Nav.Link
                          eventKey={tab.key}
                          className="d-flex align-items-center gap-2"
                        >
                          <i className={`bi ${tab.icon}`}></i>
                          <span>{tab.label}</span>
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>

                  <div className="user-info-fields card-body p-4 pt-2">
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Tab.Content className="pt-1">
                        {/* Personal Details */}
                        <Tab.Pane eventKey="Personal">
                          <div className="row g-4 mb-4">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                Username
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={user.username || user.nickname || ""}
                                disabled
                                style={{
                                  backgroundColor: "#f8f9fa",
                                  border: "1px solid #e8ebf1",
                                  ...((user.username || user.nickname) && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                }}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                Nickname
                              </label>
                              <input
                                {...register("nickname")}
                                type="text"
                                className="form-control"
                                placeholder="Enter Unique Nickname e.g. johndoe123"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(user.nickname && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.nickname && (
                                <div className="text-danger fs-12">
                                  {errors.nickname.message}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* University/Faculty & Study Year (contestants) */}
                          {auth.user.role === "contestant" && (
                            <>
                              <div className={`row g-4 mb-4`}>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold">
                                    University
                                  </label>
                                  <input
                                    {...register("university")}
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter University Name"
                                    disabled={!isEditing}
                                    style={{
                                      border: "1px solid #e8ebf1",
                                      ...(auth.user.university && {
                                        border: "1px solid rgb(149 149 149)",
                                        color: "rgb(104 104 104)",
                                      }),
                                      ...(!isEditing
                                        ? {
                                            backgroundColor: "#f8f9fa",
                                            color: "rgb(104 104 104)",
                                          }
                                        : {
                                            color: "#000",
                                            backgroundColor: "#f8f9fa",
                                            border:
                                              "1px solid rgb(107 107 107)",
                                          }),
                                    }}
                                  />
                                  {errors?.university && (
                                    <div className="text-danger fs-12">
                                      {errors.university.message}
                                    </div>
                                  )}
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label fw-bold">
                                    Faculty/College
                                  </label>
                                  <input
                                    {...register("faculty")}
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter Faculty/College Name"
                                    disabled={!isEditing}
                                    style={{
                                      ...(auth.user.faculty && {
                                        border: "1px solid rgb(149 149 149)",
                                        color: "rgb(104 104 104)",
                                      }),
                                      ...(!isEditing
                                        ? {
                                            backgroundColor: "#f8f9fa",
                                            color: "rgb(104 104 104)",
                                          }
                                        : {
                                            color: "#000",
                                            backgroundColor: "#f8f9fa",
                                            border:
                                              "1px solid rgb(107 107 107)",
                                          }),
                                    }}
                                  />
                                  {errors?.faculty && (
                                    <div className="text-danger fs-12">
                                      {errors.faculty.message}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Study Year - only show for students */}
                              {auth.user.role === "contestant" &&
                                hasJobStatus &&
                                isEffectivelyStudent && (
                                  <div
                                    className={`row g-4 mb-4 ${
                                      isEditing ? "editing" : "viewing"
                                    } ${auth.user.studyYear && "filled"}`}
                                  >
                                    <div className="col-md-12">
                                      <label
                                        htmlFor="studyYear"
                                        className="form-label font-w600"
                                      >
                                        Study Year
                                      </label>

                                      {/* Hidden input for React Hook Form */}
                                      <input
                                        type="hidden"
                                        {...register("studyYear")}
                                        value={watch("studyYear") || ""}
                                      />

                                      {/* Custom dropdown that looks like form-select */}
                                      <div
                                        ref={studyYearDropdownRef}
                                        className={`form-select form-control solid mb-1 ${
                                          studyYearDropdownOpen
                                            ? "dropdown-open"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          isEditing &&
                                          setStudyYearDropdownOpen(
                                            !studyYearDropdownOpen
                                          )
                                        }
                                        style={{
                                          cursor: isEditing
                                            ? "pointer"
                                            : "not-allowed",
                                          position: "relative",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-start",
                                        }}
                                      >
                                        <div
                                          className="dropdown-selected"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "50%",
                                            height: "100%",

                                            ...(watch("studyYear") && {
                                              color: "rgb(104 104 104)",
                                            }),
                                            ...(watch("studyYear") &&
                                              isEditing && {
                                                color: "#000",
                                              }),
                                          }}
                                        >
                                          {watch("studyYear") ||
                                            "Choose current study year..."}
                                        </div>

                                        {studyYearDropdownOpen && isEditing && (
                                          <ul
                                            className=""
                                            style={{
                                              position: "absolute",
                                              bottom: "calc(100% + 8px)", // Show above instead of below
                                              left: "2.5%",
                                              right: "0",
                                              width: "95%",
                                              zIndex: 1000,
                                              backgroundColor: "#fff",
                                              border:
                                                "1px solid rgb(216 216 216)",
                                              borderRadius: "0.75rem",
                                              maxHeight: "200px",
                                              overflowY: "auto",
                                              listStyle: "none",
                                              padding: "0",
                                              margin: "0",
                                            }}
                                          >
                                            {STUDY_YEARS.map((year, index) => (
                                              <li
                                                key={index}
                                                className="dropdown-option"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setValue("studyYear", year);
                                                  setStudyYearDropdownOpen(
                                                    false
                                                  );
                                                }}
                                                style={{
                                                  padding: "0.75rem 1rem",
                                                  cursor: "pointer",
                                                  fontSize: "0.875rem",
                                                  color: "#888888",
                                                  margin: "0",
                                                  display: "block",
                                                  width: "100%",
                                                  boxSizing: "border-box",
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.backgroundColor =
                                                    "#F93A0B";
                                                  e.target.style.color = "#fff";
                                                  e.target.style.padding =
                                                    "0.75rem 1rem";
                                                  e.target.style.margin = "0";
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.backgroundColor =
                                                    "transparent";
                                                  e.target.style.color =
                                                    "#888888";
                                                  e.target.style.padding =
                                                    "0.75rem 1rem";
                                                  e.target.style.margin = "0";
                                                }}
                                              >
                                                {year}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>

                                      {errors?.studyYear && (
                                        <div className="text-danger fs-12">
                                          {errors.studyYear.message}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                            </>
                          )}
                        </Tab.Pane>

                        {/* Contact Info */}
                        <Tab.Pane eventKey="Contact">
                          <div className="row g-4 mb-4">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                Email
                              </label>
                              <input
                                type="Enter Your Email"
                                className="form-control"
                                value={auth.user.email || user.email || ""}
                                disabled
                                style={{
                                  border: "1px solid #e8ebf1",
                                  backgroundColor: "#f8f9fa",
                                  ...(auth.user.email && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                }}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                WhatsApp Phone Number
                              </label>
                              <input
                                {...register("whatsAppPhoneNumber")}
                                type="text"
                                placeholder="Enter Unique WhatsApp Phone Number eg. +201000000000"
                                className="form-control"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(auth.user.whatsAppPhoneNumber && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.whatsAppPhoneNumber && (
                                <div className="text-danger fs-12">
                                  {errors.whatsAppPhoneNumber.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </Tab.Pane>

                        {/* Professional */}
                        <Tab.Pane eventKey="Professional">
                          <div
                            className={`row g-4 mb-4 ${
                              isEditing ? "editing" : "viewing"
                            } ${
                              auth.user.githubHandle
                                ? "filled"
                                : auth.user.behanceProfileLink
                                ? "filled"
                                : ""
                            }`}
                          >
                            <div className="col-md-12">
                              <label className="form-label fw-bold">Role</label>

                              {/* Hidden input for React Hook Form */}
                              <input
                                type="hidden"
                                {...register("position")}
                                value={watch("position") || ""}
                              />

                              <div
                                ref={positionDropdownRef}
                                className={`form-select form-control solid mb-1 ${
                                  positionDropdownOpen ? "dropdown-open" : ""
                                }`}
                                onClick={() =>
                                  isEditing &&
                                  setPositionDropdownOpen(!positionDropdownOpen)
                                }
                                style={{
                                  cursor: isEditing ? "pointer" : "not-allowed",
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  backgroundColor: !isEditing
                                    ? "#f8f9fa"
                                    : undefined,
                                }}
                              >
                                <div
                                  className="dropdown-selected"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "50%",
                                    height: "100%",
                                    ...(watch("position") &&
                                      isEditing && {
                                        color: "#000",
                                      }),
                                  }}
                                >
                                  {watch("position") || "Select a role..."}
                                </div>

                                {positionDropdownOpen && isEditing && (
                                  <ul
                                    className=""
                                    style={{
                                      position: "absolute",
                                      top: "calc(100% + 8px)",
                                      left: "2.5%",
                                      right: "0",
                                      width: "95%",
                                      zIndex: 1000,
                                      backgroundColor: "#fff",
                                      border: "1px solid rgb(216 216 216)",
                                      borderRadius: "0.75rem",
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "0",
                                      margin: "0",
                                    }}
                                  >
                                    {POSITIONS.map((position, index) => (
                                      <li
                                        key={index}
                                        className="dropdown-option"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setValue("position", position);
                                          setPositionDropdownOpen(false);
                                        }}
                                        style={{
                                          padding: "0.75rem 1rem",
                                          cursor: "pointer",
                                          fontSize: "0.875rem",
                                          color: "#888888",
                                          margin: "0",
                                          display: "block",
                                          width: "100%",
                                          boxSizing: "border-box",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor =
                                            "#F93A0B";
                                          e.target.style.color = "#fff";
                                          e.target.style.padding =
                                            "0.75rem 1rem";
                                          e.target.style.margin = "0";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor =
                                            "transparent";
                                          e.target.style.color = "#888888";
                                          e.target.style.padding =
                                            "0.75rem 1rem";
                                          e.target.style.margin = "0";
                                        }}
                                      >
                                        {position}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              {errors?.position && (
                                <div className="text-danger fs-12">
                                  {errors.position.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            className={`row g-4 mb-4 ${
                              isEditing ? "editing" : "viewing"
                            } ${auth.user.jobState && "filled"}`}
                          >
                            <div className="col-md-12">
                              <label
                                htmlFor="jobStatus"
                                className="form-label font-w600"
                              >
                                Job Status
                              </label>
                              {/* Hidden input for React Hook Form */}
                              <input
                                type="hidden"
                                {...register("jobStatus")}
                                value={watch("jobStatus") || ""}
                              />
                              {/* Always use the custom dropdown for editing */}
                              <div
                                ref={jobStatusDropdownRef}
                                className={`form-select form-control solid mb-1 ${
                                  jobStatusDropdownOpen ? "dropdown-open" : ""
                                }`}
                                onClick={() =>
                                  isEditing &&
                                  setJobStatusDropdownOpen(
                                    !jobStatusDropdownOpen
                                  )
                                }
                                style={{
                                  cursor: isEditing ? "pointer" : "not-allowed",
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  backgroundColor: !isEditing
                                    ? "#f8f9fa"
                                    : undefined,
                                }}
                              >
                                <div
                                  className="dropdown-selected"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "50%",
                                    height: "100%",
                                    ...(watch("jobStatus") &&
                                      isEditing && {
                                        color: "#000",
                                      }),
                                  }}
                                >
                                  {watch("jobStatus") || "Select your job..."}
                                </div>
                                {jobStatusDropdownOpen && isEditing && (
                                  <ul
                                    className=""
                                    style={{
                                      position: "absolute",
                                      top: "calc(100% + 8px)",
                                      left: "2.5%",
                                      right: "0",
                                      width: "95%",
                                      zIndex: 1000,
                                      backgroundColor: "#fff",
                                      border: "1px solid rgb(216 216 216)",
                                      borderRadius: "0.75rem",
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "0",
                                      margin: "0",
                                    }}
                                  >
                                    {JOB_STATUS.map((status, index) => (
                                      <li
                                        key={index}
                                        className="dropdown-option"
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          // Set the job status
                                          setValue("jobStatus", status);

                                          // Auto-set study year based on job status
                                          if (
                                            status.toLowerCase() === "student"
                                          ) {
                                            // If selecting Student, restore the original study year from database or clear it
                                            setValue(
                                              "studyYear",
                                              auth.user.studyYear || ""
                                            );
                                            // Also restore other fields to their original values
                                            setValue(
                                              "experienceYears",
                                              auth.user.experienceYears || ""
                                            );
                                            setValue(
                                              "companyName",
                                              auth.user.companyName || ""
                                            );
                                          } else {
                                            // If selecting any other job status, auto-set study year to Graduate
                                            setValue("studyYear", "Graduate");
                                            // Clear experience years and company name to let user set them fresh
                                            setValue(
                                              "experienceYears",
                                              auth.user.experienceYears || ""
                                            );
                                            setValue(
                                              "companyName",
                                              auth.user.companyName || ""
                                            );
                                          }

                                          setJobStatusDropdownOpen(false);
                                        }}
                                        style={{
                                          padding: "0.75rem 1rem",
                                          cursor: "pointer",
                                          fontSize: "0.875rem",
                                          color: "#888888",
                                          margin: "0",
                                          display: "block",
                                          width: "100%",
                                          boxSizing: "border-box",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor =
                                            "#F93A0B";
                                          e.target.style.color = "#fff";
                                          e.target.style.padding =
                                            "0.75rem 1rem";
                                          e.target.style.margin = "0";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor =
                                            "transparent";
                                          e.target.style.color = "#888888";
                                          e.target.style.padding =
                                            "0.75rem 1rem";
                                          e.target.style.margin = "0";
                                        }}
                                      >
                                        {status}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Company/Experience conditional */}
                          <>
                            {/* Company Name - show only for employed users (not freelancers) */}
                            {hasJobStatus &&
                              effectiveJobStatus
                                ?.toLowerCase()
                                .includes("employ") && (
                                <div className="row g-4 mb-4">
                                  <div className="col-md-12">
                                    <label className="form-label fw-bold">
                                      Company name
                                    </label>
                                    <input
                                      {...register("companyName")}
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Company Name"
                                      disabled={!isEditing}
                                      style={{
                                        border: "1px solid #e8ebf1",
                                        ...(auth.user.companyName && {
                                          border: "1px solid rgb(149 149 149)",
                                          color: "rgb(104 104 104)",
                                        }),
                                        ...(!isEditing
                                          ? {
                                              backgroundColor: "#f8f9fa",
                                              color: "rgb(104 104 104)",
                                            }
                                          : {
                                              color: "#000",
                                              backgroundColor: "#f8f9fa",
                                              border:
                                                "1px solid rgb(107 107 107)",
                                            }),
                                      }}
                                    />
                                    {errors?.companyName && (
                                      <div className="text-danger fs-12">
                                        {errors.companyName.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Experience level - only show for non-students */}
                            {hasJobStatus && !isEffectivelyStudent && (
                              <div
                                className={`row g-4 mb-4 ${
                                  isEditing ? "editing" : "viewing"
                                } ${auth.user.experienceYears && "filled"}`}
                              >
                                <div className="col-md-12">
                                  <div className="form-group mb-2">
                                    <label
                                      htmlFor="experienceYears"
                                      className="form-label font-w600"
                                    >
                                      Years of experience in field
                                    </label>

                                    {/* Hidden input for form registration */}
                                    <input
                                      type="hidden"
                                      {...register("experienceYears")}
                                      value={watch("experienceYears") || ""}
                                    />

                                    {/* Custom dropdown */}
                                    <div
                                      ref={experienceYearsDropdownRef}
                                      className={`form-select form-control solid mb-1 ${
                                        experienceYearsDropdownOpen
                                          ? "dropdown-open"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        isEditing &&
                                        setExperienceYearsDropdownOpen(
                                          !experienceYearsDropdownOpen
                                        )
                                      }
                                      style={{
                                        cursor: isEditing
                                          ? "pointer"
                                          : "not-allowed",
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        backgroundColor: !isEditing
                                          ? "#f8f9fa"
                                          : undefined,
                                      }}
                                    >
                                      <div
                                        className="dropdown-selected"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          width: "50%",
                                          height: "100%",
                                          ...(watch("experienceYears") &&
                                            isEditing && {
                                              color: "#000",
                                            }),
                                        }}
                                      >
                                        {watch("experienceYears")
                                          ? `${watch("experienceYears")} year${
                                              watch("experienceYears") === "1"
                                                ? ""
                                                : "s"
                                            }`
                                          : "Select Experience Level ..."}
                                      </div>

                                      {experienceYearsDropdownOpen &&
                                        isEditing && (
                                          <ul
                                            className=""
                                            style={{
                                              position: "absolute",
                                              top: "calc(100% + 8px)",
                                              left: "2.5%",
                                              right: "0",
                                              width: "95%",
                                              zIndex: 1000,
                                              backgroundColor: "#fff",
                                              border:
                                                "1px solid rgb(216 216 216)",
                                              borderRadius: "0.75rem",
                                              maxHeight: "180px",
                                              overflowY: "auto",
                                              listStyle: "none",
                                              padding: "0",
                                              margin: "0",
                                            }}
                                          >
                                            {EXPERIENCE_LEVELS.map((year) => (
                                              <li
                                                key={year}
                                                className="dropdown-option"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setValue(
                                                    "experienceYears",
                                                    year.toString()
                                                  );
                                                  setExperienceYearsDropdownOpen(
                                                    false
                                                  );
                                                }}
                                                style={{
                                                  padding: "0.5rem 0.75rem",
                                                  cursor: "pointer",
                                                  fontSize: "0.875rem",
                                                  color: "#888888",
                                                  margin: "0",
                                                  display: "block",
                                                  width: "100%",
                                                  boxSizing: "border-box",
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.backgroundColor =
                                                    "#F93A0B";
                                                  e.target.style.color = "#fff";
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.backgroundColor =
                                                    "transparent";
                                                  e.target.style.color =
                                                    "#888888";
                                                }}
                                              >
                                                {year} year
                                                {year === 1 ? "" : "s"}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                    </div>

                                    {errors?.experienceYears && (
                                      <div className="text-danger fs-12">
                                        {errors.experienceYears.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>

                          {/* Preferred quest categories */}
                          <div
                            className={`mb-4 ${
                              isEditing ? "editing" : "viewing"
                            }`}
                          >
                            <label
                              id="interestFields-label"
                              htmlFor="interestFields"
                              className="form-label font-w600"
                            >
                              Preferred quest categories
                            </label>

                            {/* Always show selected badges on top */}
                            {interestFields?.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mb-2">
                                {interestFields.map((field, index) => (
                                  <span
                                    key={index}
                                    className="interest-badge me-1 d-flex align-items-center"
                                    style={{}}
                                  >
                                    {field}
                                    {isEditing && (
                                      <i
                                        className="bi bi-x-circle ms-2"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "1rem",
                                          fontWeight: "bold",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newFields =
                                            interestFields.filter(
                                              (_, i) => i !== index
                                            );
                                          setValue("interestFields", newFields);
                                        }}
                                      ></i>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}

                            {isEditing ? (
                              <>
                                <div
                                  ref={divRef}
                                  id="interestFields"
                                  name="interestFields"
                                  className="form-select form-control d-flex align-items-center mb-1"
                                  role="button"
                                  tabIndex="0"
                                  aria-haspopup="listbox"
                                  aria-expanded={openInterestFields}
                                  aria-labelledby="interestFields-label"
                                  onClick={() =>
                                    setOpenInterestFields(!openInterestFields)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setOpenInterestFields(
                                        !openInterestFields
                                      );
                                    }
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    position: "relative",
                                    ...(isEditing && {
                                      color: "#000",
                                    }),
                                  }}
                                >
                                  {interestFields?.length === 0
                                    ? "Choose categories..."
                                    : "Add more categories..."}

                                  {openInterestFields && (
                                    <div
                                      className=""
                                      style={{
                                        position: "absolute",
                                        bottom: "calc(100% + 8px)", // Show above instead of below
                                        left: "2.5%",
                                        right: "0",
                                        width: "95%",
                                        zIndex: 1000,
                                        backgroundColor: "#fff",
                                        border: "1px solid rgb(216 216 216)",
                                        borderRadius: "0.75rem",
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        padding: "1rem",
                                        margin: "0",
                                        scrollBehavior: "smooth",
                                      }}
                                    >
                                      {FIELDS_INTEREST.map((field, index) => (
                                        <div
                                          key={index}
                                          className="form-check"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            type="checkbox"
                                            value={field}
                                            id={`field-${index}`}
                                            name="interestFields"
                                            className="form-check-input"
                                            checked={interestFields?.includes(
                                              field
                                            )}
                                            {...register("interestFields")}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <label
                                            htmlFor={`field-${index}`}
                                            className="form-check-label"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {field}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {errors?.interestFields && (
                                  <div className="text-danger fs-12">
                                    {errors.interestFields.message}
                                  </div>
                                )}
                              </>
                            ) : (
                              // Show message if no categories selected in read-only mode
                              interestFields?.length === 0 && (
                                <span className="text-muted">
                                  No interest fields selected
                                </span>
                              )
                            )}
                          </div>
                        </Tab.Pane>

                        {/* Portfolio & Links */}
                        <Tab.Pane eventKey="Portfolio">
                          <div className="row g-4 mb-4">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                LinkedIn profile URL
                              </label>
                              <input
                                {...register("linkedinProfileLink")}
                                type="text"
                                className="form-control"
                                placeholder="Enter LinkedIn Profile URL eg. https://linkedin.com/in/username"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(auth.user.linkedinProfileLink && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.linkedinProfileLink && (
                                <div className="text-danger fs-12">
                                  {errors.linkedinProfileLink.message}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                Behance profile URL
                              </label>
                              <input
                                {...register("behanceProfileLink")}
                                type="text"
                                className="form-control"
                                placeholder="Enter Unique Behance Profile URL eg. https://www.behance.net/username"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(auth.user.behanceProfileLink && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.behanceProfileLink && (
                                <div className="text-danger fs-12">
                                  {errors.behanceProfileLink.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="row g-4 mb-4">
                            <div className="col-md-12">
                              <label className="form-label fw-bold">
                                Github handle
                              </label>
                              <input
                                {...register("githubHandle")}
                                type="text"
                                className="form-control"
                                placeholder="Enter Unique Github Username eg. john_doe"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(auth.user.githubHandle && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.githubHandle && (
                                <div className="text-danger fs-12">
                                  {errors.githubHandle.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </Tab.Pane>

                        {/* Location */}
                        <Tab.Pane eventKey="Location">
                          <div className="row g-4 mb-4">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">
                                Country
                              </label>
                              <input
                                {...register("country")}
                                type="text"
                                className="form-control"
                                placeholder="Enter Country Name"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(auth.user.country && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.country && (
                                <div className="text-danger fs-12">
                                  {errors.country.message}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">City</label>
                              <input
                                {...register("city")}
                                type="text"
                                className="form-control"
                                placeholder="Enter City Name"
                                disabled={!isEditing}
                                style={{
                                  border: "1px solid #e8ebf1",
                                  ...(auth.user.city && {
                                    border: "1px solid rgb(149 149 149)",
                                    color: "rgb(104 104 104)",
                                  }),
                                  ...(!isEditing
                                    ? {
                                        backgroundColor: "#f8f9fa",
                                        color: "rgb(104 104 104)",
                                      }
                                    : {
                                        color: "#000",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid rgb(107 107 107)",
                                      }),
                                }}
                              />
                              {errors?.city && (
                                <div className="text-danger fs-12">
                                  {errors.city.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </Tab.Pane>
                      </Tab.Content>
                    </form>
                  </div>
                </Tab.Container>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Removed old About Me and bottom Edit button */}
      <ToastContainer />
    </Fragment>
  );
};

export default AppProfile;
